/**
 * American BioLabs — experiment arm bridge (ng1: Shopify vs Next split).
 *
 * Active only for visitors who arrive with a valid `abx` token from the Hair
 * Insider split (or who stored one earlier). It:
 * - keeps the token for 60 days (localStorage + first-party cookie `abl_abx`,
 *   which the checkout custom pixel reads),
 * - mirrors it to private cart attributes `__abx` / `__abx_at`, which carry
 *   into the order,
 * - sends funnel steps (store_view, add_to_cart) and the product page's load timings
 *   (store_timing) to the experiment collector.
 *
 * The token is a random ID and an arm letter. It holds no ad identifiers and
 * is never sent to ad platforms. Independent of abl-google-attribution.js; it
 * never intercepts cart adds or checkout routing.
 */
(function () {
  'use strict';

  if (window.ABLExperiment) return;

  var TOKEN_RE = /^ng1\.[ab]\.[0-9a-f]{16}$/;
  var KEY = 'abl_abx';
  var MAX_AGE_S = 60 * 24 * 60 * 60;
  var COLLECTOR = 'https://thehairinsider.com/e';

  function readCookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
    if (!m) return '';
    try { return decodeURIComponent(m[1]); } catch (e) { return ''; }
  }

  function load() {
    var fromUrl = '';
    try { fromUrl = new URLSearchParams(location.search).get('abx') || ''; } catch (e) {}
    if (TOKEN_RE.test(fromUrl)) return { token: fromUrl, at: new Date().toISOString(), fresh: true };
    var stored = null;
    try { stored = JSON.parse(localStorage.getItem(KEY) || 'null'); } catch (e) {}
    if (stored && TOKEN_RE.test(stored.token || '')) return { token: stored.token, at: stored.at || '', fresh: false };
    var c = readCookie(KEY);
    if (TOKEN_RE.test(c)) return { token: c, at: '', fresh: false };
    return null;
  }

  var state = load();
  if (!state) return;

  if (state.fresh) {
    try { localStorage.setItem(KEY, JSON.stringify({ token: state.token, at: state.at })); } catch (e) {}
  }
  document.cookie = KEY + '=' + encodeURIComponent(state.token) + '; Max-Age=' + MAX_AGE_S +
    '; Path=/; SameSite=Lax' + (location.protocol === 'https:' ? '; Secure' : '');

  var parts = state.token.split('.');

  function beacon(step, extra) {
    try {
      var body = JSON.stringify({ e: parts[0], a: parts[1], v: parts[2], s: step, p: 'shopify', u: location.pathname, x: extra || {} });
      if (navigator.sendBeacon) navigator.sendBeacon(COLLECTOR, body);
      else fetch(COLLECTOR, { method: 'POST', body: body, keepalive: true, mode: 'no-cors' });
    } catch (e) {}
  }

  function attributes() {
    return { __abx: state.token, __abx_at: state.at || '' };
  }

  function cartRoute() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root + 'cart/update.js';
  }

  var syncing = null;
  function sync() {
    if (syncing) return syncing;
    syncing = fetch(cartRoute(), {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ attributes: attributes() })
    }).catch(function () {}).then(function () { syncing = null; });
    return syncing;
  }

  /* Same pattern as abl-google-attribution.js: add hidden fields to the cart
     checkout form so the attribute survives even if the AJAX sync was late. */
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    var submitter = event.submitter || (document.activeElement && document.activeElement.form === form ? document.activeElement : null);
    if (!submitter || submitter.name !== 'checkout') return;
    var attrs = attributes();
    Object.keys(attrs).forEach(function (name) {
      var field = 'attributes[' + name + ']';
      var input = form.elements.namedItem(field);
      if (!(input instanceof HTMLInputElement)) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = field;
        form.appendChild(input);
      }
      input.value = attrs[name];
    });
  }, true);

  document.addEventListener('cart:added', function () {
    sync();
    beacon('add_to_cart');
  });

  /* Page speed as the browser measures it, with the same code on the Next funnel, so the two stores
     compare like for like (store_view fires at a different moment on each). Milliseconds from the
     start of the navigation: ttfb = first byte of the page, fcp = first content painted, dcl = HTML
     parsed, lcp = largest element painted. Measured on the first store page in the tab and sent once,
     as store_timing, when LCP is final: the first click or key press, or the page going to the
     background (which includes leaving it). Pages loaded in a background tab are skipped. */
  function watchTimings() {
    var perf = window.performance;
    if (!perf || !perf.getEntriesByType || document.visibilityState === 'hidden') return;
    try {
      if (sessionStorage.getItem(KEY + '_t')) return;
      sessionStorage.setItem(KEY + '_t', '1');
    } catch (e) {}
    var nav = perf.getEntriesByType('navigation')[0];
    var start = (nav && nav.activationStart) || 0; // prerendered: count from when it was shown
    var lcp = 0, observer = null, sent = false;
    function ms(t) { return t > 0 ? Math.round(Math.max(0, t - start)) : null; }
    function take(list) { list.forEach(function (e) { lcp = e.startTime; }); }
    try {
      observer = new PerformanceObserver(function (l) { take(l.getEntries()); });
      observer.observe({ type: 'largest-contentful-paint', buffered: true });
    } catch (e) { observer = null; }
    function send(event) {
      if (sent || (event && event.isTrusted === false)) return;
      if (event && event.type === 'visibilitychange' && document.visibilityState !== 'hidden') return;
      sent = true;
      if (observer) { try { take(observer.takeRecords()); observer.disconnect(); } catch (e) {} }
      var fcp = perf.getEntriesByName('first-contentful-paint')[0];
      beacon('store_timing', {
        lcp: ms(lcp), fcp: fcp ? ms(fcp.startTime) : null,
        ttfb: nav ? ms(nav.responseStart) : null, dcl: nav ? ms(nav.domContentLoadedEventStart) : null,
        nav: (nav && nav.type) || ''
      });
    }
    ['keydown', 'click', 'visibilitychange', 'pagehide'].forEach(function (type) {
      (type === 'pagehide' ? window : document).addEventListener(type, send, true);
    });
  }

  var main = document.getElementById('MainContent');
  var template = (main && main.getAttribute('data-template')) || '';
  if (/product/.test(template)) {
    beacon('store_view', { template: template, fresh: state.fresh });
    if (document.prerendering) document.addEventListener('prerenderingchange', watchTimings, { once: true });
    else watchTimings();
  }

  sync();

  /* Microsoft Clarity custom tags (same keys on the Next funnel) so recordings, heatmaps and
     funnels can be filtered by arm. The stub queues calls until the Clarity app's tag loads. */
  try {
    window.clarity = window.clarity || function () { (window.clarity.q = window.clarity.q || []).push(arguments); };
    window.clarity('set', 'abx_exp', parts[0]);
    window.clarity('set', 'abx_arm', parts[1]);
    window.clarity('set', 'funnel_platform', 'shopify');
  } catch (e) {}

  window.ABLExperiment = { token: state.token, version: '1.2.0' };
})();
